import { useEffect, useRef } from "react";
import { ArrowRight, Phone, CheckCircle } from "lucide-react";
import { business } from "../../config/business";
import { trackEvent } from "../../lib/gtag";
import Button from "../ui/Button";

export default function Hero() {
  const ref = useRef(null);

  useEffect(() => {
    const items = ref.current?.querySelectorAll(".fade-in-up") ?? [];
    items.forEach((el, i) => {
      setTimeout(() => el.classList.add("is-visible"), 150 + i * 150);
    });
  }, []);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center bg-navy-900 overflow-hidden pt-28 pb-20"
    >
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Accent glow blobs */}
      <div className="absolute top-1/4 right-1/4 w-125 h-125 bg-accent-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 left-1/5 w-80 h-80 bg-navy-700/60 rounded-full blur-2xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 w-full">
        <div className="max-w-3xl">
          {/* Stars trust badge */}
          <div className="fade-in-up inline-flex items-center gap-2.5 bg-navy-800/70 border border-navy-700 rounded-full px-4 py-2 mb-7">
            <span className="text-accent-400 text-base tracking-tight select-none">
              ★★★★★
            </span>
            <span className="text-ink-300 text-sm font-medium">
              5.0 · {business.reviewCount} Google reviews
            </span>
          </div>

          {/* Headline */}
          <h1 className="fade-in-up font-heading font-extrabold text-white text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.08] tracking-tight mb-6">
            London's Trusted
            <br />
            <span className="text-accent-400">Heating &amp; Cooling</span> Team
          </h1>

          {/* Subheadline */}
          <p className="fade-in-up text-ink-300 text-lg md:text-xl leading-relaxed mb-9 max-w-2xl">
            G2 licensed and TSSA authorized technicians serving London, Ontario
            and surrounding communities. Expert installations, reliable repairs
            — available 24 hours, 7 days a week.
          </p>

          {/* CTAs */}
          <div className="fade-in-up flex flex-col sm:flex-row gap-4 mb-10">
            <Button variant="primary" size="lg" as="a" href="#contact">
              Request a Free Quote
              <ArrowRight size={18} />
            </Button>
            <Button
              variant="outline-white"
              size="lg"
              as="a"
              href={`tel:${business.phone}`}
              onClick={() =>
                trackEvent("phone_call_click", { method: "tel_link" })
              }
            >
              <Phone size={18} />
              Call {business.phoneDisplay}
            </Button>
          </div>

          {/* Micro trust indicators */}
          <div className="fade-in-up flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-400">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-success-500 inline-block" />
              Open 24 Hours
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={13} className="text-accent-400" />
              TSSA Authorized
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={13} className="text-accent-400" />
              G2 Licensed &amp; ODP Certified
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
